/**
 * Tên role + các nhóm quyền dùng chung ở FE.
 *
 * Bản sao có chủ đích của `backend/app/core/roles.py` (ROLE_* và `BUSINESS_ROLES`).
 * Backend mới đổi guard `require_staff_or_admin` sang ALLOW-LIST; FE thì còn ba chỗ
 * nhốt riêng `accountant` bằng DENY-LIST (`!isAccountant`…), nên role mới
 * thấy đủ menu và vào được mọi route, chỉ vỡ khi API trả 403. Không phải lỗ hổng bảo
 * mật (backend đã chặn) nhưng là UX hỏng — và cùng kiểu sai đó sẽ lặp lại với mỗi role
 * thêm sau này.
 *
 * ĐẶT Ở `constants/` chứ không ở `state/` hay `helpers/`: đây là dữ liệu tĩnh, không
 * có state và không gọi API. Ba nơi dùng nó (router guard, menu, thanh công cụ đơn
 * hàng) đều import trực tiếp, không đi qua Vuex, nên không nơi nào dựng lại danh
 * sách role cho riêng mình.
 *
 * QUY TẮC: mọi nhóm dưới đây là allow-list. Thêm role mới thì mặc định KHÔNG có gì;
 * phải chủ động khai vào nhóm tương ứng. Đừng viết lại thành "trừ role X ra".
 *
 * VIẾT BẰNG CommonJS (`.cjs`) để `node --test` require thẳng được (cùng khuôn
 * `helpers/shipFormStores.cjs`): luật khoá vùng ở đây có một bất biến chống vòng lặp
 * redirect cần test canh, mà bản ESM thì test node thuần không nạp nổi. `roles.js` bên
 * cạnh chỉ là lớp vỏ re-export cho phía Vue import như cũ.
 */

// Bản CTV chỉ có BA role — printer (Nhân viên in) và foreman (Quản đốc) đã gỡ
// 2026-10-02 (backend: core/roles.py + migration f3a4b5c6d7e8).
const ROLE_ADMIN = "admin";
const ROLE_STAFF = "staff";
const ROLE_ACCOUNTANT = "accountant";

/**
 * Nhãn tiếng Việt của từng role, hiện trên giao diện (dropdown chọn vai trò khi thêm
 * nhân viên…).
 *
 * ĐẶT CẠNH các hằng ROLE_* chứ không nằm trong màn hình dùng nó: bảng này trước đây
 * viết tay trong `views/apps/ecommerce/staff.vue`, nên khi thêm role mới mà quên khai
 * ở đó thì dropdown rơi về fallback `r.name` và hiện tên role tiếng Anh lẫn giữa các
 * nhãn tiếng Việt. Để chung chỗ với chính danh sách role thì lần thêm role sau khó
 * quên hơn.
 *
 * Khoá là TÊN role (ổn định), không phải id — id là auto-increment nên khác nhau giữa
 * các môi trường.
 */
const ROLE_LABELS = Object.freeze({
  [ROLE_ADMIN]: "Quản trị",
  [ROLE_STAFF]: "Nhân viên",
  [ROLE_ACCOUNTANT]: "Kế toán",
});

/** Nhãn tiếng Việt của role; role lạ (chưa khai ở trên) thì trả về chính tên role. */
const roleLabel = (roleName) => ROLE_LABELS[roleName] || roleName || "";

/**
 * Role có trong bảng nhãn ở trên không — dropdown chọn vai trò chỉ hiện role này.
 * Role cũ còn sót trong bảng `roles` trên DB (trước khi migration gỡ chạy) không
 * được chọn cho nhân viên mới: tài khoản đó sẽ không vào được module nào.
 */
const isKnownRole = (roleName) =>
  !!roleName && Object.prototype.hasOwnProperty.call(ROLE_LABELS, roleName);

/**
 * Role làm nghiệp vụ (đơn hàng, khách hàng, sản phẩm…). Gương của `BUSINESS_ROLES`
 * ở backend — sửa một bên thì sửa bên kia, nếu không menu sẽ hiện những mục mà API
 * trả 403.
 */
const BUSINESS_ROLES = Object.freeze([ROLE_ADMIN, ROLE_STAFF]);

/** Module công nợ `/accounting/*` — cổng backend: require_roles(ACCOUNTANT, ADMIN). */
const ACCOUNTING_ROLES = Object.freeze([ROLE_ADMIN, ROLE_ACCOUNTANT]);

/**
 * Ai được đặt/sửa giá gốc sản phẩm trong bảng giá.
 * Gương của `PRICE_WRITE_ROLES` ở backend.
 */
const PRICE_WRITE_ROLES = Object.freeze([ROLE_ADMIN]);

/**
 * Ai được đặt/sửa đơn giá từng dòng hàng trong đơn. Gương của
 * `ORDER_ITEM_PRICE_WRITE_ROLES` ở backend.
 */
const ORDER_ITEM_PRICE_WRITE_ROLES = Object.freeze([ROLE_ADMIN, ROLE_STAFF]);

/**
 * Ai được đổi trạng thái thanh toán và số tiền đã thanh toán của đơn. Gương của
 * `PAYMENT_WRITE_ROLES` ở backend — kế toán có tên trong danh sách dù hôm nay họ bị
 * khoá trong vùng `/accounting/*` và làm việc đó qua phiếu thu.
 */
const PAYMENT_WRITE_ROLES = Object.freeze([ROLE_ADMIN, ROLE_ACCOUNTANT]);

/**
 * Ai được khai tiền thu hộ (COD) khác số hệ thống tự tính. Gương của
 * `COD_WRITE_ROLES` ở backend — tách khỏi `PAYMENT_WRITE_ROLES`: staff có quyền
 * COD nhưng không có quyền payment; kế toán thì ngược lại.
 */
const COD_WRITE_ROLES = Object.freeze([ROLE_ADMIN, ROLE_STAFF]);

/** Ai được bật Freeship khi tạo/sửa đơn hoặc gửi vận đơn. Gương của `FREESHIP_WRITE_ROLES`. */
const FREESHIP_WRITE_ROLES = Object.freeze([ROLE_ADMIN]);

/**
 * Thao tác GHI HÀNG LOẠT trên danh sách đơn (gửi vận đơn, xoá, đổi trạng thái…).
 * Bản CTV hiện trùng BUSINESS_ROLES; tách riêng vì đây là quyết định khác (thao tác
 * hàng loạt), role nghiệp vụ mới thêm sau không mặc nhiên có nó.
 */
const ORDER_BULK_ACTION_ROLES = Object.freeze([ROLE_ADMIN, ROLE_STAFF]);

/**
 * Role bị KHOÁ trong đúng một khu vực: vào bất kỳ route nào khác thì bị đá về trang
 * chủ của khu vực đó. Mỗi mục là { prefix, home, confinePublicRoutes? }.
 *
 * Gom thành bảng thay vì viết hai khối `if` giống nhau trong router: khối thứ hai
 * copy từ khối thứ nhất là chỗ dễ quên nhất — quên đúng một dấu `!` là thành vòng
 * lặp redirect, và triệu chứng (trang trắng) không chỉ về dòng code nào cả.
 *
 * `confinePublicRoutes: true` nghĩa là role bị khoá vùng NGAY CẢ trên route công khai
 * (authRequired: false) khi đã đăng nhập.
 *
 * BẢN CTV: bảng này RỖNG. Vùng `/accounting/*` của kế toán là của CMS tổng, không
 * có route ở bản này — khoá vùng thì đăng nhập bằng role đó rơi vào trang trắng
 * không thoát ra được. (Quản đốc và vùng `/delivery-schedule` đã gỡ hẳn khỏi bản CTV.) Công nợ của CTV là
 * trang CHỈ-ĐỌC nằm trong vùng chung, không cần khoá vùng riêng.
 */
const CONFINED_ROLE_AREAS = Object.freeze({});

/** Role có bị khoá vùng ngay cả trên route công khai không (xem giải thích ở trên). */
const confinesOnPublicRoutes = (roleName) => !!CONFINED_ROLE_AREAS[roleName]?.confinePublicRoutes;

/** Có role này trong danh sách cho phép không. */
const roleAllowed = (roleName, allowed) => !!roleName && allowed.includes(roleName);

/**
 * Route mà MỌI role vào được, kể cả role bị khoá vùng.
 *
 * Không có danh sách này thì `accountant` bị đá khỏi /pages/profile-setting (nó không
 * nằm trong /accounting/) — tức là role đó KHÔNG ĐỔI ĐƯỢC MẬT KHẨU, trong khi menu vẫn
 * hiện link: bấm vào là bật ngược về trang chủ khu vực, không một dòng thông báo.
 */
const SHARED_ROUTE_PREFIXES = Object.freeze(["/pages/profile-setting", "/logout"]);

const isSharedRoute = (path) =>
  SHARED_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Role bị khoá vùng đang đi ra ngoài vùng ⇒ trả về đường dẫn phải đá về; `null` = cho qua.
 *
 * BẤT BIẾN: `home` của mỗi khu vực PHẢI nằm trong chính `prefix` của nó, nếu không lần
 * redirect nào cũng lại rơi ra ngoài vùng và guard đá vòng quanh vô tận (triệu chứng chỉ
 * là trang trắng, không chỉ về dòng code nào). Có test canh bất biến này.
 */
const confinedRedirect = (roleName, path) => {
  const area = CONFINED_ROLE_AREAS[roleName];
  if (!area) return null;
  if (typeof path !== "string" || path === "") return area.home;
  const childPrefix = area.prefix.endsWith("/") ? area.prefix : `${area.prefix}/`;
  if (path === area.prefix || path.startsWith(childPrefix)) return null;
  if (isSharedRoute(path)) return null;
  return area.home;
};

module.exports = {
  ACCOUNTING_ROLES,
  COD_WRITE_ROLES,
  FREESHIP_WRITE_ROLES,
  PAYMENT_WRITE_ROLES,
  PRICE_WRITE_ROLES,
  ROLE_LABELS,
  BUSINESS_ROLES,
  CONFINED_ROLE_AREAS,
  ORDER_ITEM_PRICE_WRITE_ROLES,
  ORDER_BULK_ACTION_ROLES,
  ROLE_ACCOUNTANT,
  ROLE_ADMIN,
  ROLE_STAFF,
  SHARED_ROUTE_PREFIXES,
  confinedRedirect,
  confinesOnPublicRoutes,
  isKnownRole,
  isSharedRoute,
  roleAllowed,
  roleLabel,
};
